<template>
    <VJoyPanel v-if="profile" ref="profileHeaderSectionRef" class="profile-header-section" padding="large" @mouseup="handleTextSelection">
        <template #panel-body>
            <VJoyWrapper :align="'flex-start'" class="profile-header-section__wrapper" column-gap="6" row-gap="6" wrap="nowrap">
                <div class="profile-header-section__avatar-container">
                    <VJoyAvatar :full-name="displayName" :photo-url="photoUrl" class="profile-header-section__avatar" loading="eager" size="xlarge" />
                    <slot :mobile="false" name="availability" />
                    <slot name="avatar-overlay" />
                </div>
                <slot name="wishlist" />
                <VJoyWrapper :align="'flex-start'" class="profile-header-section__content" direction="column" no-margin row-gap="2">
                    <VJoyWrapper column-gap="4" no-margin row-gap="4">
                        <h1 class="profile-header-section__name" data-testid="profile-fullname">
                            {{ displayName }}
                        </h1>
                        <ProfileStrategyBadge
                            v-if="isStrategyProfile"
                            :title="$t('profile.header.strategy.badge.title')"
                            data-testid="profile-strategy-badge"
                        />
                        <BadgeReadSection :badge="profile.badge" :firstname="profile.firstname" />
                        <RqthBadge v-if="rqthProfileEnabled && profile.rqthEligible && profile.rqthDisplayEnabled" />
                        <slot :mobile="true" name="availability" />
                    </VJoyWrapper>
                    <h2 :title="profile.headline" class="profile-header-section__headline" data-testid="profile-headline">
                        {{ profile.headline }}
                    </h2>
                    <VJoyWrapper class="profile-header-section__indicators" no-margin row-gap="1">
                        <slot name="first-indicator" />
                        <ProjectIndicator v-if="missionCount > 0" :nb-projects="missionCount" />
                        <slot name="third-indicator" />
                        <LocationIndicator :address="profile.address" data-testid="profile-location-preference-address" />
                        <ExperienceLevelIndicator :experience-level="profile.experienceLevel" data-testid="profile-header-experience-level" />
                    </VJoyWrapper>
                    <div :id="anchorsPageProfile.topSkillsSetSection" class="profile-header-section__skills top-skills-section">
                        <slot name="skills">
                            <SkillsReadOnly
                                v-if="hasTopSkills"
                                :is-visible-seo-link="isVisibleSeoLink"
                                :is-visible-translated-label="isVisibleTranslatedLabel"
                                :skills="topSkillsToSkills!"
                                class="profile-header-section__top-skills"
                                data-testid="profile-header-section-top-skills"
                                gap="small"
                            />
                        </slot>
                    </div>
                </VJoyWrapper>
                <slot name="actions" />
            </VJoyWrapper>
        </template>
    </VJoyPanel>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyAvatar, VJoyPanel, VJoyWrapper} from '@maltjoy/core-vue';
    import type {Skill} from '@api';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {anchorsPageProfile} from '~/config/anchors';
    import {useAppraisalStore} from '~/features/appraisals/store/appraisalStore';
    import {useTranslationStore} from '~/store/translation/translationStore';
    import {ProfileVertical} from '~/types/domain/Profile';
    import BadgeReadSection from '../BadgeReadSection.vue';
    import ExperienceLevelIndicator from '../indicators/ExperienceLevelIndicator.vue';
    import LocationIndicator from '../indicators/LocationIndicator.vue';
    import ProjectIndicator from '../indicators/ProjectIndicator.vue';
    import ProfileStrategyBadge from '../ProfileStrategyBadge.vue';
    import RqthBadge from '../RqthBadge.vue';
    import SkillsReadOnly from '@/components/_common/skills/SkillsReadOnly.vue';
    import {useStrategyProfile} from '@/composables/useStrategyProfile';
    import {useProfileStore} from '@/store/profileStore';
    import {useSkillSetStore} from '@/store/skill-set/skillSetStore';

    const rqthProfileEnabled = useFeatureFlag('rqth-profile-badge');

    const profileStore = useProfileStore();
    const {displayName, profile} = storeToRefs(profileStore);
    const {appraisals} = storeToRefs(useAppraisalStore());

    const {profileTopSkills} = storeToRefs(useSkillSetStore());
    const hasTopSkills = computed(() => profileTopSkills.value && profileTopSkills.value.length > 0);
    const topSkillsToSkills = computed<readonly Skill[]>(() => profileTopSkills.value);

    const {isTranslationActivated, isTranslationDone} = storeToRefs(useTranslationStore());
    const isVisibleSeoLink = computed(() => profile.value?.vertical === ProfileVertical.FREELANCER);
    const isVisibleTranslatedLabel = computed((): boolean => isTranslationActivated.value && isTranslationDone.value);

    const photoUrl = computed(() =>
        profile.value?.photo?.baseUrl
            ? `${profile.value?.photo?.baseUrl}/${profile.value?.photo?.name}?gravity=face&func=face&face_margin=70&w=360&h=360&force_format=webp`
            : '',
    );

    const isStrategyProfile = computed(() => useStrategyProfile());
    const missionCount = computed(() => appraisals.value?.missionCount ?? 0);

    const {compareCopiedTextWithFreelancerName} = useProfileTracking();
    function handleTextSelection() {
        compareCopiedTextWithFreelancerName(displayName.value);
    }
</script>

<style lang="scss" scoped>
    .profile-header-section {
        container-type: inline-size;
        container-name: profile-header-section;

        &__wrapper {
            flex-direction: row;

            @container profile-header-section (max-width: 750px) {
                flex-direction: column;
            }
        }

        &__avatar-container {
            position: relative;
        }

        &__avatar {
            @container profile-header-section (min-width: 581px) {
                --avatar-size: 184px !important;
                --avatar-font-size: var(--joy-font-size-primary-800) !important;
            }
        }

        &__content {
            flex: 1;
        }

        &__name {
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-600);
            line-height: var(--joy-line-height-large);
            color: var(--joy-color-primary-50);
            margin: 0;
            overflow: hidden;
            display: -webkit-box;
            line-clamp: 2;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }

        &__headline {
            margin: 0;
            overflow-wrap: anywhere;
            font-weight: var(--joy-font-weight-bold);
            font-size: var(--joy-font-size-primary-600);
            line-height: var(--joy-line-height-large);
            color: var(--joy-color-text-heading);
            text-align: left;
            text-transform: capitalize;
            text-overflow: ellipsis;
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            line-clamp: 3;
            -webkit-line-clamp: 3;
        }

        &__indicators {
            color: var(--joy-color-text-heading);
            margin-top: var(--joy-core-spacing-2);
        }

        &__skills {
            margin-top: var(--joy-core-spacing-2);
            flex: unset;
            width: 100%;
        }
    }
</style>
